// components/VerifyThroughPlaid.tsx
import React, { CSSProperties, ReactNode, forwardRef, useState } from "react";
import { useGetOne } from "react-admin";
import { plaidCopy } from '../../../utils/plaidConfig';

import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import StopSignIcon from "../components/StopSignIcon";
import {scrollToAnchor} from "./scrollToAnchor";
import PlaidIncomeLink from './PlaidIncomeLink';

const PLAID_ENABLEMENT_RESOURCES = {
  bank: 'plaid/plaidBankIncomeEnabled',
  payroll: 'plaid/plaidPayrollIncomeEnabled',
} as const;

type Props = {
  trn_id?: string | null;
  triggerLabel?: string;
  includeRelinkWarning?: boolean;
  linkType?: "bank" | "payroll";
  onDone?: () => void;
  sourceId?: number;
  triggerContainerClassName?: string;
  triggerContainerStyle?: CSSProperties;
  triggerRowClassName?: string;
  triggerRowStyle?: CSSProperties;
  triggerButtonClassName?: string;
  triggerButtonStyle?: CSSProperties;
  triggerIcon?: ReactNode;
  disabled?: boolean;
  onFlowStarted?: () => void;
  onPlaidSuccess?: () => void;
  onFlowExited?: (completed: boolean) => void;
};

export const VerifyThroughPlaid = forwardRef<HTMLDivElement, Props>(
  ({
    trn_id,
    triggerLabel,
    includeRelinkWarning = false,
    linkType = "bank",
    onDone,
    sourceId,
    triggerContainerClassName,
    triggerContainerStyle,
    triggerRowClassName,
    triggerRowStyle,
    triggerButtonClassName,
    triggerButtonStyle,
    triggerIcon,
    disabled = false,
    onFlowStarted,
    onPlaidSuccess,
    onFlowExited,
  }, ref) => {
  const enablementResource = PLAID_ENABLEMENT_RESOURCES[linkType] ?? PLAID_ENABLEMENT_RESOURCES.bank;
  const { data: plaidIncomeEnabled, isLoading, isError } = useGetOne(
    enablementResource,
    { id: trn_id },
    { enabled: trn_id != null, refetchOnWindowFocus: true }
  );

  // Dialog state
  const [altOpen, setAltOpen] = useState(false);
  const handleOpenAlt = () => setAltOpen(true);
  const handleCloseAlt = () => setAltOpen(false);

  // Called when Plaid completes successfully
  const handleLinkClicked = () => {
    handleCloseAlt();
    onFlowStarted?.();
  };

  const handlePlaidDone = () => {
    if (linkType === "payroll") {
      onDone?.();
      return;
    }
    setAltOpen(false);
    scrollToAnchor("bank-income-anchor", 3000);
    onDone?.();
  };

  const handleGoToEmploymentIncome = (event: React.MouseEvent<HTMLAnchorElement>) => {
    event.preventDefault();
    setAltOpen(false);
    scrollToAnchor(`employment-income-anchor-${trn_id}`, 3000);
  };

  if (!trn_id || isLoading || isError || !plaidIncomeEnabled?.enabled) return null;

  const triggerText = triggerLabel ?? 'Provide proof another way';
  const showBankWarning = linkType === "bank";

  const copyVariant = plaidCopy[linkType];
  const dialogBody = (
    <div style={{ lineHeight: 1.5 }}>
      <p>{copyVariant.plaidActionDescription}</p>
      <br />
      <p>{copyVariant.plaidActionWarning}</p>
      {includeRelinkWarning && (
        <>
          <br />
          <p>{copyVariant.plaidActionRelinkWarning}</p>
        </>
      )}
    </div>
  );

  const containerClassName =
    triggerContainerClassName || "d-flex flex-column align-items-end justify-content-begin";
  const containerStyle = triggerContainerStyle || { width: "100%" };
  const rowClassName = triggerRowClassName || "d-flex justify-content-begin";
  const rowStyle = triggerRowStyle || { width: "100%", marginTop: "8px" };
  const buttonClassName =
    triggerButtonClassName || "btn btn-link p-0 text-decoration-none";
  const buttonStyle = triggerButtonStyle || { fontSize: "15px" };

  return (
    <div
      ref={ref}
      className={containerClassName}
      style={containerStyle}
    >
      <div className={rowClassName} style={rowStyle}>
        <button
          type="button"
          className={buttonClassName}
          style={buttonStyle}
          onClick={handleOpenAlt}
          disabled={disabled}
          aria-label={triggerText}
        >
          {triggerIcon ? (
            <>
              {triggerIcon}
              <span style={{ fontFamily: "inherit", fontWeight: "inherit", fontSize: "inherit" }}>
                {triggerText}
              </span>
            </>
          ) : (
            triggerText
          )}
        </button>
      </div>

      <Dialog
        open={altOpen}
        onClose={handleCloseAlt}
        aria-labelledby="verify-alt-title"
        aria-describedby="verify-alt-desc"
        disableScrollLock
        PaperProps={{ style: { maxWidth: 420 } }}
      >
        <DialogContent id="verify-alt-desc">
          {showBankWarning && (
            <div
              className="d-flex align-items-start"
              style={{
                gap: "10px",
                marginBottom: "0.75rem",
                padding: "10px 12px",
                borderRadius: 8,
                backgroundColor: "#fff3cd",
                color: "#664d03",
                lineHeight: 1.5,
              }}
            >
              <StopSignIcon size={24} />
              <div>
                Do not proceed if you have already uploaded original pdf paystubs.
                If you have them available to upload, you can do so{" "}
                <a href={`#employment-income-anchor-${trn_id}`} onClick={handleGoToEmploymentIncome}>
                  here
                </a>
                .
              </div>
            </div>
          )}

          <div className="d-flex align-items-center" style={{ gap: "0.5rem", marginBottom: "0.5rem" }}>
            {!showBankWarning && (
              <svg
                className="flex-shrink-0"
                width="32"
                height="32"
                viewBox="0 0 48 48"
                style={{ minWidth: 32, minHeight: 32 }}
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <g fill="#111" fillRule="evenodd">
                  <path d="M18.637 0L4.09 3.81.081 18.439l5.014 5.148L0 28.65l3.773 14.693 14.484 4.047 5.096-5.064 5.014 5.147 14.547-3.81 4.008-14.63-5.013-5.146 5.095-5.063L43.231 4.13 28.745.083l-5.094 5.063L18.637 0zM9.71 6.624l7.663-2.008 3.351 3.44-4.887 4.856L9.71 6.624zm16.822 1.478l3.405-3.383 7.63 2.132-6.227 6.187-4.808-4.936zM4.672 17.238l2.111-7.705 6.125 6.288-4.886 4.856-3.35-3.44zm29.547-1.243l6.227-6.189 1.986 7.74-3.404 3.384-4.809-4.935zm-15.502-.127l4.887-4.856 4.807 4.936-4.886 4.856-4.808-4.936zm-7.814 7.765l4.886-4.856 4.81 4.936-4.888 4.856-4.808-4.936zm15.503.127l4.886-4.856L36.1 23.84l-4.887 4.856-4.807-4.936zM4.57 29.927l3.406-3.385 4.807 4.937-6.225 6.186-1.988-7.738zm14.021 1.598l4.887-4.856 4.808 4.936-4.886 4.856-4.809-4.936zm15.502.128l4.887-4.856 3.351 3.439-2.11 7.705-6.128-6.288zm-24.656 8.97l6.226-6.189 4.81 4.936-3.406 3.385-7.63-2.133zm16.843-1.206l4.886-4.856 6.126 6.289-7.662 2.007-3.35-3.44z" />
                </g>
              </svg>
            )}

            <h2
              id="verify-alt-title"
              style={{ margin: 0, fontSize: "1rem", fontWeight: 600, lineHeight: 1.4 }}
            >
              Verify your income
            </h2>
          </div>

          {/* Body text below header */}
          {dialogBody}
        </DialogContent>

        <DialogActions className="plaid-dialog-actions">
          <div className="plaid-dialog-actions__item">
            <button
              type="button"
              onClick={handleCloseAlt}
              className="close-btn"
            >
              Close
            </button>
          </div>

          <div className="plaid-dialog-actions__item">
            <PlaidIncomeLink
              type={linkType}
              trn_id={trn_id}
              linkTokenData={sourceId ? { source_id: sourceId } : undefined}
              disabled={disabled}
              onLinkClicked={handleLinkClicked}
              onPlaidSuccess={onPlaidSuccess}
              onPlaidExit={onFlowExited}
              onDone={handlePlaidDone}
              subtitle=""
              label="Continue"
              asButton
              className="plaid-btn"
              style={{
                width: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                flex: 1,
                minWidth: 0,
              }}
            />
          </div>
        </DialogActions>

        <style>{`
          .plaid-dialog-actions {
            display: flex;
            gap: 4px;
            width: 100%;
            align-items: stretch;
            justify-content: space-between;
          }
          
          .plaid-dialog-actions__item {
            flex: 1 1 0;
            display: flex;
            min-width: 0;
          }
          
          .plaid-dialog-actions__item > * {
            flex: 1;
            min-width: 0;
          }
          
          .plaid-btn {
            padding: 10px 32px;
            border: 1px solid #1976d2;
            border-radius: 8px;
            background: #1976d2;     /* was #fff */
            color: #fff;             /* was #1976d2 */
            font-weight: 600;
            font-size: 0.95rem;
            text-decoration: none;
            cursor: pointer;
            box-shadow: 0 1px 2px rgba(0,0,0,0.08);
            transition: background .15s ease, border-color .15s ease, box-shadow .15s ease, transform .05s ease;
            white-space: nowrap;   
            flex: 1;
            min-width: 0;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }
          .plaid-btn:hover {
            background: #1565c0;
            border-color: #1565c0;
            box-shadow: 0 2px 6px rgba(0,0,0,0.18);
            color: #fff !important;
            text-decoration: none !important;
          }
          
          .plaid-btn:active {
            background: #0d47a1;
            border-color: #0d47a1;
            transform: translateY(1px);
            box-shadow: 0 1px 2px rgba(0,0,0,0.12);
            color: #fff !important;
            text-decoration: none !important;
          }
          
          /* Independent style for Close */
          .close-btn {
            padding: 10px 16px;
            border: 1px solid #cbd5e1;
            border-radius: 8px;
            background: #fff;
            color: #334155;
            font-weight: 600;
            font-size: 0.95rem;
            text-decoration: none;
            cursor: pointer;
            box-shadow: 0 1px 2px rgba(0,0,0,0.06);
            transition: background .15s ease, border-color .15s ease, box-shadow .15s ease, transform .05s ease;
            width: 100%;
            display: inline-flex;
            flex: 1;
            min-width: 0;
            align-items: center;
            justify-content: center;
          }
          .close-btn:hover {
            background: #f8fafc;
            border-color: #94a3b8;
            box-shadow: 0 2px 6px rgba(0,0,0,0.12);
          }
          .close-btn:active {
            background: #f1f5f9;
            border-color: #94a3b8;
            transform: translateY(1px);
            box-shadow: 0 1px 2px rgba(0,0,0,0.10);
          }
        `}</style>
      </Dialog>
    </div>
  );
  }
);

export default VerifyThroughPlaid;
